import React from 'react';

import { AlertContainer } from '../../components/Alert/Alert';
import { colors, darkThemeSelector, styled } from '../../stitches.config';

export const FieldContainerBase = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeAll: colors.strokeNeutralDark,
  },

  borderRadius: '$10',

  [`& > div:first-child:not([data-alert-container])`]: {
    paddingTop: '$12',
    borderRadiusTop: '$10',
  },

  [`& > ${AlertContainer}:first-child`]: {
    paddingTop: '$8',
    borderRadiusTop: '$10',
  },

  [`& > div:not(:first-child)`]: {
    borderRadiusTop: 0,
  },

  [`& > div:last-child:not([data-alert-container])`]: {
    paddingBottom: '$12',
    borderRadiusBottom: '$10',
  },

  [`& > div:last-child ${AlertContainer}`]: {
    paddingBottom: '$8',
    borderRadiusBottom: '$10',
  },

  [`& > div:not(:last-child)`]: {
    borderRadiusBottom: 0,
  },

  variants: {
    background: {
      application: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
      surface: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
      neutral: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
  },
});
FieldContainerBase.displayName = 'FieldContainerBase';

const FieldContainerContext = React.createContext(false);

export function useFieldContainerContext() {
  return React.useContext(FieldContainerContext);
}

export type FieldContainerProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  background?: 'application' | 'surface' | 'neutral';
};

export function FieldContainer({ background = 'surface', ref, ...props }: FieldContainerProps) {
  return (
    <FieldContainerContext.Provider value>
      <FieldContainerBase background={background} {...props} ref={ref} />
    </FieldContainerContext.Provider>
  );
}

export function MaybeFieldContainer({
  background = 'surface',
  children,
}: {
  background?: 'application' | 'surface' | 'neutral';
  children: React.ReactNode;
}) {
  const isInsideFieldContainer = useFieldContainerContext();
  const WrapperEl = isInsideFieldContainer ? React.Fragment : FieldContainer;
  const wrapperProps = isInsideFieldContainer ? {} : { background };
  return <WrapperEl {...wrapperProps}>{children}</WrapperEl>;
}
